import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CollapseBlock } from '../../../../common/index.jsx';
import { Alert, Col, Form as AntdForm, Row } from 'antd';
import Form from '../../../../antd/form/Form/Form.jsx';
import { useTranslation } from 'react-i18next';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch.jsx';
import FormDate from '../../../../antd/form/FormDate/FormDate.jsx';
import FormTextEditor from '../../../../antd/form/FormTextEditor/FormTextEditor.jsx';
import { required, url } from '../../../../antd/form/FormValidation.js';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import dayjs from 'dayjs';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';

const EventForm = ({
  initialValues,
  onSubmit,
  newsCollection,
  articlesCollection,
}) => {
  const { t } = useTranslation();
  const { apiErrorMessage: showErrorMessage } = useToastMessages();

  const [form] = AntdForm.useForm();
  const { setFieldsValue, getFieldValue } = form;

  const watchFrom = AntdForm.useWatch('from', form);
  const isEventChecked = AntdForm.useWatch('P', form);

  const disabledDateTo = (current) => {
    if (!watchFrom) {
      return current.isBefore(dayjs(), 'day');
    }
    return watchFrom.isAfter(current, 'day'); // Disable all dates before day of 'from' date
  };

  const specialEventsOptions = useMemo(() => {
    return [
      {
        value: 'Začiatok zimného semestra',
        label: t('Začiatok zimného semestra'),
      },
      { value: 'Koniec zimného semestra', label: t('Koniec zimného semestra') },
      {
        value: 'Začiatok letného semestra',
        label: t('Začiatok letného semestra'),
      },
      { value: 'Koniec letného semestra', label: t('Koniec letného semestra') },
    ];
  }, [t]);

  const articlesOptions = useMemo(() => {
    return (
      articlesCollection &&
      articlesCollection.map((article) => ({
        label: article.title_sk,
        value: article.id,
      }))
    );
  }, [articlesCollection]);

  const newsOptions = useMemo(() => {
    return (
      newsCollection &&
      newsCollection.map((news) => ({
        label: news.title_sk,
        value: news.id,
      }))
    );
  }, [newsCollection]);

  const handleFinish = (values) => {
    const news_id = form.getFieldValue('news_id') || '';
    const article_id = form.getFieldValue('article_id') || '';
    const ext_link = form.getFieldValue('ext_link') || '';
    const filled = [news_id, article_id, ext_link].filter(
      (v) => v && v !== '',
    ).length;
    if (filled > 1) {
      showErrorMessage(
        t(
          'Súčasne je možné vyplniť len jedno z polí: Súvisiaca aktualita, Súvisiaci článok alebo Súvisiaca URL adresa.',
        ),
      );
      return;
    }
    // Validate at least one of B, E, D, P is checked
    const B = form.getFieldValue('B');
    const E = form.getFieldValue('E');
    const D = form.getFieldValue('D');
    const P = form.getFieldValue('P');
    if (!B && !E && !D && !P) {
      showErrorMessage(
        t(
          'Musí byť zaškrtnutý aspoň jeden typ udalosti (Bakalárska, Inžinierska, Doktorandská, Podujatie).',
        ),
      );
      return;
    }
    onSubmit(values);
  };

  return (
    <>
      <Form
        onFinish={handleFinish}
        initialValues={initialValues}
        form={form}
        layout="vertical"
      >
        <CollapseBlock title={t('Obsah')} collapsible collapse>
          <FormTextEditor
            label={t('Názov udalosti (slovensky)')}
            name={'title_sk'}
            height={300}
            rules={[required(t)]}
          />
          <FormTextEditor
            label={t('Názov udalosti (anglicky)')}
            name={'title_en'}
            height={300}
          />

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <FormDate
                label={t('Začiatok udalosti')}
                name={'from'}
                //defaultValue={initialValues?.from}
                rules={[required(t)]}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={12}>
              <FormDate
                label={t('Koniec udalosti')}
                name={'to'}
                //defaultValue={initialValues?.to}
                style={{ width: '100%' }}
                rules={[
                  {
                    validator: async (_, to) => {
                      const from = getFieldValue('from');
                      if (from && to && dayjs(to).isBefore(from, 'day')) {
                        return Promise.reject(
                          t('Koniec udalosti nesmie byť skôr ako začiatok'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                disabledDate={disabledDateTo}
              />
            </Col>
          </Row>
        </CollapseBlock>
        <CollapseBlock title={t('Typ udalosti')} collapsible collapse>
          <Row gutter={[8, 0]}>
            <Col span={6}>
              <FormSwitch label={t('Bakalárska')} name={'B'} />
            </Col>
            <Col span={6}>
              <FormSwitch label={t('Inžinierska')} name={'E'} />
            </Col>
            <Col span={6}>
              <FormSwitch label={t('Doktorandská')} name={'D'} />
            </Col>
            <Col span={6}>
              <FormSwitch label={t('Podujatie')} name={'P'} />
            </Col>
          </Row>
          {isEventChecked && (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <FormSelect
                    label={
                      <label className={'note-to-field'}>
                        {' '}
                        {t('Súvisiaca aktualita')}{' '}
                      </label>
                    }
                    name={`news_id`}
                    options={newsOptions}
                    showSearch
                    rules={[]}
                  />
                </Col>
                <Col span={8}>
                  <FormSelect
                    label={
                      <label className={'note-to-field'}>
                        {' '}
                        {t('Súvisiaci článok')}{' '}
                      </label>
                    }
                    name={`article_id`}
                    options={articlesOptions}
                    showSearch
                    rules={[]}
                  />
                </Col>
                <Col span={8}>
                  <FormTextField
                    label={
                      <label className={'note-to-field'}>
                        {' '}
                        {t('Súvisiaca URL adresa')}{' '}
                      </label>
                    }
                    name={'ext_link'}
                    rules={[url(t)]}
                  />
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Alert
                    className={'note-to-field'}
                    title={t('Súčasne je možné vyplniť len jedno z polí.')}
                    type="warning"
                  />
                </Col>
              </Row>
            </>
          )}
        </CollapseBlock>
        <CollapseBlock
          title={t('Špeciálne nastavenia')}
          collapsible
          collapse
          closedByDefault={true}
        >
          <Row>
            <Col span={8}>
              <FormSelect
                label={t('Špeciálna udalosť')}
                name={'special_event'}
                options={specialEventsOptions}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
          <FormTextField type={'hidden'} name={'ext_link'} />
        </CollapseBlock>
        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'calendar'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </Form>
    </>
  );
};

EventForm.propTypes = {
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func,
  articleCollections: PropTypes.array,
  articlesCollection: PropTypes.array,
};

export default EventForm;
